import { publicEnv } from "../config/publicEnv";

/**
 * Communicatie met de Webflow-pagina die deze widget als iframe embedt.
 * Stuurt nooit persoonsgegevens mee en gebruikt nooit "*" als doel-origin -
 * alleen de daadwerkelijke parent-origin, en alleen wanneer die voorkomt in
 * de toegestane lijst (VITE_ALLOWED_PARENT_ORIGINS).
 */

let cachedTrustedOrigin: string | null | undefined;

export function getTrustedParentOrigin(): string | null {
  if (cachedTrustedOrigin !== undefined) return cachedTrustedOrigin;

  if (window.self === window.top) {
    // Niet in een iframe geladen (bijv. lokale ontwikkeling); niets te melden.
    cachedTrustedOrigin = null;
    return null;
  }

  try {
    if (!document.referrer) {
      cachedTrustedOrigin = null;
      return null;
    }
    const referrerOrigin = new URL(document.referrer).origin;
    cachedTrustedOrigin = publicEnv.allowedParentOrigins.includes(referrerOrigin)
      ? referrerOrigin
      : null;
  } catch {
    cachedTrustedOrigin = null;
  }

  return cachedTrustedOrigin;
}

function postToParent(message: Record<string, unknown>): void {
  const targetOrigin = getTrustedParentOrigin();
  if (!targetOrigin || !window.parent) return;
  window.parent.postMessage(message, targetOrigin);
}

export function notifyResize(heightPx: number): void {
  postToParent({ type: "dmm:resize", height: Math.ceil(heightPx) });
}

export function notifyStepChange(step: 1 | 2 | 3): void {
  postToParent({ type: "dmm:step-change", step });
}

export function notifyRequestSubmitted(): void {
  postToParent({ type: "dmm:request-submitted", event: "request_submitted" });
}

/** Houdt de hoogte van `element` in de gaten en meldt wijzigingen aan de parent. */
export function observeHeight(element: HTMLElement): () => void {
  notifyResize(element.offsetHeight);

  const observer = new ResizeObserver((entries) => {
    for (const entry of entries) {
      notifyResize(entry.contentRect.height + getVerticalPadding(element));
    }
  });
  observer.observe(element);

  return () => observer.disconnect();
}

function getVerticalPadding(element: HTMLElement): number {
  const style = window.getComputedStyle(element);
  return parseFloat(style.paddingTop || "0") + parseFloat(style.paddingBottom || "0");
}
